import { filter, first, get, isEmpty } from "lodash";
import { CogIcon, PlusIcon } from "lucide-react";
import { FC, useCallback, useMemo } from "react";
import { Link, useParams } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { UniqueTabData } from "@/data/types";
import { useCMSStore } from "@/hooks/use-cms-store";

import { useCMSTabContext } from "../cms-tab-context";
import { ColumnMappingRow } from "./column-mapping-row";
import { ContentEmptyAlert } from "./content-empty-alert";
import { useUniqueTabContext } from "./unique-tab-context";

const HIDDEN_COLUMNS = ["tag_uid"];

// Generate a reasonably unique key for the column mapping
// There is no way to make it perfectly unique, but it is okay to treat two
// identical column mappings as the same.
const generateColumnMappingKey = (
  { options: { column_name, column_type, is_downloadable } }: UniqueTabData,
  index: number,
) => {
  return `${column_name}-${column_type}-${is_downloadable}-${index}`;
};

export const UniqueTabContent: FC = () => {
  const { skuId } = useParams<{ skuId: string }>();
  const { tabIndex } = useCMSTabContext();
  const { data } = useUniqueTabContext();
  const uniqueContent = useCMSStore.use.uniqueContent();
  const onAdd = useCMSStore.use.onAddUniqueTabColumnMapping();

  const allColumnNames = useMemo(
    () =>
      filter(
        get(uniqueContent, "metadata.column_names", []),
        (name) => !HIDDEN_COLUMNS.includes(name),
      ),
    [uniqueContent],
  );

  const handleAddColumnMapping = useCallback(() => {
    const firstColumnName = first(allColumnNames);

    // This should be unreachable, based on the button's disabled state.
    if (!firstColumnName) return;

    // Set the first column name as the default column name, to prevent
    // submitting intermediate states where the column name is empty.
    onAdd(tabIndex, firstColumnName);
  }, [onAdd, tabIndex, allColumnNames]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-medium leading-none">
          Content
        </CardTitle>
        <CardDescription>
          Go to <b>Advanced</b> to upload a .csv with the correct format and
          content that you would like to map to each unique tag.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {isEmpty(data) ? (
          <ContentEmptyAlert hasUniqueContent={!isEmpty(allColumnNames)} />
        ) : (
          <div className="space-y-2">
            {data.map((column, index) => (
              <ColumnMappingRow
                key={generateColumnMappingKey(column, index)}
                index={index}
                columnName={column.options.column_name}
                allColumnNames={allColumnNames}
                columnType={column.options.column_type}
                hasLabel={index === 0}
                isDownloadable={column.options.is_downloadable}
              />
            ))}
          </div>
        )}

        <Button
          variant="secondary"
          className="w-full"
          disabled={isEmpty(allColumnNames) || isEmpty(uniqueContent)}
          onClick={handleAddColumnMapping}
        >
          <PlusIcon className="size-4" /> Add content
        </Button>
      </CardContent>

      <div className="border-t px-6 py-4">
        <Button variant="secondary" asChild>
          <Link to={`/skus/${skuId}/web-experience/advanced`}>
            <CogIcon /> Go to Advanced
          </Link>
        </Button>
      </div>
    </Card>
  );
};
